// src/pages/admin/Building/BuildingImages.jsx
import React, { useState } from 'react';
import {
  FaUpload,
  FaTrash,
  FaStar,
  FaTimes,
  FaSpinner,
  FaImage,
  FaCheckCircle,
} from 'react-icons/fa';
import { successToast, errorToast } from '../../Utils/toast';
import {
  useUploadBuildingImagesMutation,
  useDeleteBuildingImageMutation,
} from '../../redux/services/buildingService/building.Services';

function BuildingImages({ data }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [uploadImages] = useUploadBuildingImagesMutation();
  const [deleteImage] = useDeleteBuildingImageMutation();

  const building = data;
  const images = building?.images || [];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const removeSelectedFile = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
  };

  const handleUploadImages = async () => {
    if (selectedFiles.length === 0) {
      errorToast('Please select images to upload');
      return;
    }

    try {
      setUploading(true);
      const formDataToSend = new FormData();
      selectedFiles.forEach((file) => {
        formDataToSend.append('images', file);
      });

      await uploadImages({ id: building.id, data: formDataToSend }).unwrap();
      successToast('Images uploaded successfully');
      setSelectedFiles([]);
      // Refresh the page to show new images
      window.location.reload();
    } catch (error) {
      errorToast(error?.data?.message || 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      setDeleting(imageId);
      await deleteImage(imageId).unwrap();
      successToast('Image deleted successfully');
      // Remove image from list
      const updatedImages = images.filter(img => img.id !== imageId);
      building.images = updatedImages;
      // Force re-render
      window.location.reload();
    } catch (error) {
      errorToast(error?.data?.message || 'Failed to delete image');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="building-images-container">
      <div className="mb-3">
        <h5 className="fw-bold">
          <FaImage className="me-2" />
          {building?.name} - Images
        </h5>
        <p className="text-muted">Manage images for this building</p>
      </div>

      {/* Upload Section */}
      <div className="card mb-4">
        <div className="card-body">
          <h6 className="fw-bold mb-3">📤 Upload New Images</h6>
          <div className="row g-3">
            <div className="col-md-8">
              <input
                type="file"
                className="form-control"
                onChange={handleFileChange}
                multiple
                accept="image/*"
              />
              <small className="text-muted">You can select multiple images (JPG, PNG, WEBP)</small>
            </div>
            <div className="col-md-4">
              <button
                className="btn btn-success w-100"
                onClick={handleUploadImages}
                disabled={uploading || selectedFiles.length === 0}
              >
                {uploading ? (
                  <>
                    <FaSpinner className="spinning me-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FaUpload className="me-2" />
                    Upload {selectedFiles.length} Images
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="mt-3">
              <div className="d-flex flex-wrap gap-2">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="badge bg-primary d-flex align-items-center gap-2 p-2">
                    <span>{file.name}</span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-white p-0"
                      onClick={() => removeSelectedFile(index)}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Images Grid */}
      {images.length === 0 ? (
        <div className="text-center py-5">
          <FaImage className="text-muted fs-1 mb-3" />
          <h5>No Images</h5>
          <p className="text-muted">Upload images to showcase this building</p>
        </div>
      ) : (
        <div className="row g-3">
          {images.map((image) => (
            <div key={image.id} className="col-md-3 col-6">
              <div className="card h-100">
                <div className="position-relative">
                  <img
                    src={image.image_url}
                    alt={image.caption || 'Building image'}
                    className="card-img-top"
                    style={{ height: '180px', objectFit: 'cover' }}
                  />
                  {image.is_primary && (
                    <span className="position-absolute top-0 start-0 badge bg-primary m-2">
                      <FaStar className="me-1" /> Primary
                    </span>
                  )}
                  <button
                    className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
                    onClick={() => handleDeleteImage(image.id)}
                    disabled={deleting === image.id}
                  >
                    {deleting === image.id ? (
                      <FaSpinner className="spinning" />
                    ) : (
                      <FaTrash />
                    )}
                  </button>
                </div>
                <div className="card-body p-2">
                  <small className="text-muted d-block">
                    {image.caption || 'No caption'}
                  </small>
                  <small className="text-muted">
                    {image.is_primary ? (
                      <span className="text-success">
                        <FaCheckCircle className="me-1" /> Primary
                      </span>
                    ) : (
                      <button
                        className="btn btn-link btn-sm p-0 text-primary"
                        onClick={() => {
                          // Handle set as primary
                          if (window.confirm('Set this image as primary?')) {
                            // API call to set as primary
                          }
                        }}
                      >
                        Set as Primary
                      </button>
                    )}
                  </small>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Tips */}
      <div className="mt-4">
        <div className="alert alert-info">
          <h6 className="fw-bold mb-2">📌 Tips for Images</h6>
          <ul className="mb-0">
            <li>Use high-quality images (minimum 800x600 pixels)</li>
            <li>First uploaded image is automatically set as primary</li>
            <li>You can set any image as primary by clicking "Set as Primary"</li>
            <li>Supported formats: JPG, PNG, WEBP</li>
            <li>Maximum file size: 5MB per image</li>
          </ul>
        </div>
      </div>

      <style jsx>{`
        .building-images-container {
          padding: 5px 0;
        }
        .card {
          transition: all 0.3s;
        }
        .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .spinning {
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default BuildingImages;